import { BaseCatalogItemTransformer } from './baseCatalogItem.transformer';
import type { Artwork } from '@/types';
import type { CatalogType } from '../services/catalog/catalog-types';
import { getMediaData, getVideoOrFileData } from '@/lib/utils/media.utils';
import { getBlockchainTag, getTokenStandardTag, getYearTag, getMarketplaceTag, getCategoryTags, combineTags } from '@/lib/utils/tagGenerator';

/**
 * Transformer for converting Artwork entities to CatalogItem
 */
export class ArtworkTransformer extends BaseCatalogItemTransformer<Artwork> {
  protected getId(artwork: Artwork): string | number {
    return artwork.id;
  }

  protected getType(_artwork: Artwork): CatalogType {
    return 'artwork';
  }

  protected getHref(artwork: Artwork): string {
    return `/catalog/artwork/${artwork.id}`;
  }

  protected getTitle(artwork: Artwork): string {
    return artwork.name;
  }

  protected getDisplayTitle(artwork: Artwork): string {
    return artwork.name;
  }

  protected getFilename(artwork: Artwork): string {
    return artwork.name;
  }

  protected getThumbnail(artwork: Artwork): string {
    const cardImageData = getMediaData(artwork.nftDisplayImage);
    // Fallback to URL fields if upload is empty
    return cardImageData.url || artwork.nftDisplayImageUrl || artwork.nftImageUrl || '';
  }

  protected getImageUrl(artwork: Artwork): string {
    const cardImageData = getMediaData(artwork.nftDisplayImage);
    const bannerImageData = getMediaData(artwork.nftImage);
    const cardImage = cardImageData.url || '';
    // Fallback to URL fields if uploads are empty
    return bannerImageData.url || cardImage || artwork.nftImageUrl || artwork.nftDisplayImageUrl || '';
  }

  protected getWidth(artwork: Artwork): number {
    const cardImageData = getMediaData(artwork.nftDisplayImage);
    const bannerImageData = getMediaData(artwork.nftImage);
    return bannerImageData.width || cardImageData.width || 800;
  }

  protected getHeight(artwork: Artwork): number {
    const cardImageData = getMediaData(artwork.nftDisplayImage);
    const bannerImageData = getMediaData(artwork.nftImage);
    return bannerImageData.height || cardImageData.height || 800;
  }

  protected getDate(artwork: Artwork): string {
    return artwork.artworkCreatedDate || artwork.createdAt;
  }

  protected getYear(artwork: Artwork): number {
    const date = artwork.artworkCreatedDate || artwork.createdAt;
    return new Date(String(date)).getFullYear();
  }

  protected getDescription(artwork: Artwork): string | undefined {
    return artwork.description || undefined;
  }

  protected getLongDescription(artwork: Artwork): string | undefined {
    return artwork.description || undefined;
  }

  protected getPrice(artwork: Artwork): number {
    return artwork.currentPrice || 0;
  }

  protected getAvailable(_artwork: Artwork): boolean {
    return true;
  }

  protected getVideoUrl(artwork: Artwork): string | undefined {
    const videoData = artwork.nftVideo ? getVideoOrFileData(artwork.nftVideo) : null;
    // Fallback to animation URL field if upload is empty
    return videoData?.url || artwork.nftDisplayAnimationUrl || undefined;
  }

  protected getHtmlUrl(artwork: Artwork): string | undefined {
    const fileData = artwork.nftFile && typeof artwork.nftFile !== 'number'
      ? getMediaData(artwork.nftFile)
      : null;
    return fileData?.url && fileData.url.toLowerCase().endsWith('.html')
      ? fileData.url
      : undefined;
  }

  protected getArtworkCount(_artwork: Artwork): number {
    return 1;
  }

  protected getCollectorCount(artwork: Artwork): number {
    return artwork.totalSales || 0;
  }

  protected getWalletAddress(artwork: Artwork): string | undefined {
    return artwork.contract || undefined;
  }

  protected getArtist(artwork: Artwork): string | undefined {
    if (artwork.currentOwner && typeof artwork.currentOwner !== 'number') {
      return artwork.currentOwner.name || undefined;
    }
    return undefined;
  }

  protected getLastSalePrice(artwork: Artwork): number | undefined {
    return artwork.lastSalePrice || undefined;
  }

  protected getSymbol(artwork: Artwork): string | undefined {
    if (artwork.blockchain && typeof artwork.blockchain !== 'number') {
      return artwork.blockchain.symbol || undefined;
    }
    return undefined;
  }

  protected getTags(artwork: Artwork): string[] {
    return combineTags(
      getBlockchainTag(artwork.blockchain),
      getTokenStandardTag(artwork.standard),
      artwork.oneofone ? 'ONEOFONE' : null,
      getYearTag(artwork.artworkCreatedDate, artwork.createdAt),
      getMarketplaceTag(artwork.marketplace),
      getCategoryTags(artwork.artCategory),
      // Include parent collection categories if available
      artwork.collection && typeof artwork.collection !== 'number'
        ? getCategoryTags(artwork.collection.artCategory)
        : []
    );
  }

  getCategories(artwork: Artwork): string[] {
    const categories: string[] = [];

    // Direct categories
    if (artwork?.artCategory && Array.isArray(artwork.artCategory)) {
      artwork.artCategory.forEach(category => {
        if (category && typeof category === 'object' && 'title' in category) {
          categories.push(category.title);
        }
      });
    }

    // Parent collection categories
    if (
      artwork.collection &&
      typeof artwork.collection !== 'number' &&
      artwork.collection.artCategory &&
      typeof artwork.collection.artCategory !== 'number' &&
      Array.isArray(artwork.collection.artCategory)
    ) {
      artwork.collection.artCategory.forEach(category => {
        if (category && typeof category === 'object' && 'title' in category) {
          categories.push(category.title);
        }
      });
    }

    return categories;
  }

  protected getMarketname(artwork: Artwork): string | undefined {
    if (artwork.marketplace) {
      const marketname =
        typeof artwork.marketplace === 'number'
          ? String(artwork.marketplace)
          : typeof artwork.marketplace === 'object' && 'title' in artwork.marketplace
            ? artwork.marketplace.title
            : '';
      return marketname || undefined;
    }
    return undefined;
  }

  // Series info from parent collection
  protected getSeriesId(artwork: Artwork): number | null {
    if (artwork.collection && typeof artwork.collection !== 'number') {
      return artwork.collection.id;
    }
    return typeof artwork.collection === 'number' ? artwork.collection : null;
  }

  protected getSeriesTitle(artwork: Artwork): string | null {
    if (artwork.collection && typeof artwork.collection !== 'number') {
      return artwork.collection.name;
    }
    return null;
  }

  protected getSeriesSlug(artwork: Artwork): string | null {
    if (artwork.collection && typeof artwork.collection !== 'number') {
      return artwork.collection.slug;
    }
    return null;
  }

  protected getSeriesHref(artwork: Artwork): string | null {
    if (artwork.collection && typeof artwork.collection !== 'number') {
      return `/catalog/collections/${artwork.collection.slug}`;
    }
    return null;
  }

  protected getSeriesYear(artwork: Artwork): number | null {
    if (artwork.collection && typeof artwork.collection !== 'number') {
      const date = artwork.collection.createdDate || artwork.collection.startDate || artwork.collection.createdAt;
      if (date) {
        return new Date(String(date)).getFullYear();
      }
    }
    return null;
  }

  // Additional Artwork-specific methods for extended properties
  getDetailTags(artwork: Artwork): (string | { title: string; url: string })[] {
    const detailTags: (string | { title: string; url: string })[] = [];

    // Add basic tags using tagGenerator functions
    const blockchainTag = getBlockchainTag(artwork.blockchain);
    if (blockchainTag) detailTags.push(blockchainTag);

    const tokenStandardTag = getTokenStandardTag(artwork.standard);
    if (tokenStandardTag) detailTags.push(tokenStandardTag);

    if (artwork.oneofone) {
      detailTags.push('ONEOFONE');
    }

    const yearTag = getYearTag(artwork.artworkCreatedDate, artwork.createdAt);
    if (yearTag) detailTags.push(yearTag);

    // Categories
    const categories = this.getCategories(artwork);
    categories.forEach(cat => detailTags.push(cat));

    // Marketplace with link
    const marketplaceTag = getMarketplaceTag(artwork.marketplace);
    if (marketplaceTag) {
      if (artwork.marketLink) {
        detailTags.push({
          title: marketplaceTag,
          url: artwork.marketLink,
        });
      } else {
        detailTags.push(marketplaceTag);
      }
    }

    return detailTags;
  }

  // Override toCatalogItem to add extra fields
  toCatalogItem(artwork: Artwork) {
    const catalogItem = super.toCatalogItem(artwork);

    // Add additional artwork-specific fields
    const traits = artwork.traits || [];

    // File size
    const fileSize =
      typeof artwork.nftImage !== 'number'
        ? artwork.nftImage?.filesize
        : typeof artwork.nftFile !== 'number'
          ? artwork.nftFile?.filesize
          : undefined;

    // File format
    let fileFormat = '';
    if (artwork.nftFile && typeof artwork.nftFile !== 'number') {
      fileFormat = artwork.nftFile.filename?.split('.').pop()?.toUpperCase() || '';
    } else if (artwork.nftImage && typeof artwork.nftImage !== 'number') {
      fileFormat = artwork.nftImage.filename?.split('.').pop()?.toUpperCase() || '';
    }

    // Resolution
    let resolution = '';
    if (artwork.nftImage && typeof artwork.nftImage !== 'number' && artwork.nftImage.mimeType?.includes('svg')) {
      resolution = 'Vector';
    }

    let currentOwnerAddress = '';
    if (artwork.currentOwner && typeof artwork.currentOwner !== 'number') {
      currentOwnerAddress = artwork.currentOwner.mainWalletAddress || '';
    }

    // Aspect ratio with priority logic
    const artworkAspectRatio =
      artwork.artworkAspectRatio ||
      (artwork.collection && typeof artwork.collection !== 'number'
        ? artwork.collection.artworkAspectRatio
        : null) ||
      null;

    return {
      ...catalogItem,
      traits,
      fileSize,
      fileFormat,
      resolution,
      currentPrice: artwork.currentPrice,
      totalSales: artwork.totalSales,
      currentOwnerAddress,
      lastSaleDate: artwork.lastSaleDate ?? null,
      artworkAspectRatio,
    };
  }
}

// Export singleton instance
export const artworkTransformer = new ArtworkTransformer();